import React, { useState } from 'react';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import format from 'date-fns/format';
import Button, { LoadingButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import BookIcon from '@atlaskit/icon/glyph/book';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import ScreenIcon from '@atlaskit/icon/glyph/screen';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import TeamsIcon from '@atlaskit/icon/glyph/teams';
import WorldIcon from '@atlaskit/icon/glyph/world';
import { Flex, Stack, Text } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import { useIntl } from '@atlassian/jira-intl';
import { useResource } from '@atlassian/jira-router';
import { transitioningDetailsResource } from '@atlassian/jira-router-resources-eoc-unlock-operations';
import stepFourVectorDark from '../../../../../common/assets/step_four_vector_dark.png';
import stepFourVectorLight from '../../../../../common/assets/step_four_vector_light.png';
import { IconBoxColor, IconBox } from '../../../../../common/ui/icon-box';
import { WizardInfoItem } from '../../../../../common/ui/wizard/info-item';
import { Layout } from '../../../../../common/ui/wizard/layout';
import { messages as commonMessages } from '../../../../../common/ui/wizard/layout/messages';
import { useGlobalChangeboarding } from '../../../../../controllers/store/global';
import { useUserTimezone } from '../../../../../controllers/timezone';
import { messages } from './messages';
import type { StepFourProps } from './types';

export const StepFour = ({ onAction, onClose, onUnlock }: StepFourProps) => {
	const { formatMessage } = useIntl();
	const { data: transitioningData } = useResource(transitioningDetailsResource);
	const { timeZone } = useUserTimezone();
	const { activateWelcomeModal } = useGlobalChangeboarding();

	if (!transitioningData) {
		throw new Error(
			'[Optimal StepFour] component should not be rendered before details resource fetched',
		);
	}

	const [isUnlocking, setIsUnlocking] = useState(false);

	const handleNext = () => {
		activateWelcomeModal();

		setIsUnlocking(true);
		onUnlock?.()
			.then(() => {
				onAction((prevState) => prevState + 1);
			})
			.finally(() => {
				setIsUnlocking(false);
			});
	};

	const primaryActionButton = (
		<LoadingButton onClick={handleNext} appearance="primary" isLoading={isUnlocking}>
			{formatMessage(messages.primaryButtonTextNonFinal)}
		</LoadingButton>
	);

	const secondaryActionButton = (
		<Button onClick={() => onAction((prevState) => prevState - 1)} appearance="default">
			{formatMessage(commonMessages.backButtonNonFinal)}
		</Button>
	);

	return (
		<Layout
			vector={{ light: stepFourVectorLight, dark: stepFourVectorDark }}
			currentStep={4}
			actionButtons={{
				primary: primaryActionButton,
				secondary: secondaryActionButton,
			}}
			onClose={onClose}
		>
			<Flex direction="column" rowGap="space.200">
				<Flex rowGap="space.100" direction="column">
					<Heading size="large">{formatMessage(messages.titleNonFinal)}</Heading>
					<Text>{formatMessage(messages.descriptionNonFinal)}</Text>
				</Flex>
				<Stack space="space.100">
					<WizardInfoItem
						iconBox={<IconBox icon={CopyIcon} color={IconBoxColor.Magenta} withCustomContainer />}
						description={formatMessage(messages.firstItemNonFinal)}
					/>
					<WizardInfoItem
						iconBox={<IconBox icon={BookIcon} color={IconBoxColor.Purple} withCustomContainer />}
						description={formatMessage(messages.secondItemNonFinal)}
					/>
					<WizardInfoItem
						iconBox={<IconBox icon={ScreenIcon} color={IconBoxColor.Teal} withCustomContainer />}
						description={formatMessage(messages.thirdItemNonFinal, {
							unlockDate: format(
								utcToZonedTime(transitioningData?.autoOgTurnOffDate, timeZone),
								'dd MMMM, yyyy',
							),
						})}
					/>
					<WizardInfoItem
						iconBox={<IconBox icon={WorldIcon} color={IconBoxColor.Blue} withCustomContainer />}
						description={formatMessage(messages.fourthItemNonFinal, {
							link: (str: React.ReactNode) => (
								<Anchor
									href="https://support.atlassian.com/security-and-access-policies/docs/understand-data-residency/"
									target="_blank"
								>
									{str}
								</Anchor>
							),
						})}
					/>
					<WizardInfoItem
						iconBox={<IconBox icon={TeamsIcon} color={IconBoxColor.Orange} withCustomContainer />}
						description={formatMessage(messages.fifthItemNonFinal)}
					/>
				</Stack>
				<Text>
					{formatMessage(messages.dontReadyYetNonFinal, {
						link: (
							<Anchor
								href="https://support.atlassian.com/jira-service-management-cloud/docs/start-shifting-from-opsgenie-to-jira-service-management/"
								target="_blank"
							>
								{formatMessage(messages.learnMoreNonFinal)}
								<ShortcutIcon label="" size="small" />
							</Anchor>
						),
					})}
				</Text>
			</Flex>
		</Layout>
	);
};
